import { NotificationItem } from './notification-item';
import { ChatMessageRI, SectionBasicRI, StreamActivityChatMessageRI, UserSectionBasicRI } from 'common-utils/rest-interfaces';
import { pdsI18n } from '../pds-i18n';

export type CommunicationNotificationItemContext = {
  activity: StreamActivityChatMessageRI,
  userType: string,
  currentUserUid: string,
  getStreamGroupId: string,
  senderInfo: UserSectionBasicRI
};

export class CommunicationNotificationItem extends NotificationItem {
  static NOTIFICATION_TYPE = 'COMMUNICATION_NOTIFICATION';

  private sectionToDetailMap: Map<string, SectionBasicRI>;

  static getIsPartOfThread(threadUid: string): (notification: CommunicationNotificationItem) => boolean {
    return (notification: CommunicationNotificationItem) => {
      return notification.getThreadUid() === threadUid && !notification.isRead;
    };
  }

  static generateLookUpKey(topicUid: string, threadOwnerUid: string): string {
    return [topicUid, threadOwnerUid, CommunicationNotificationItem.NOTIFICATION_TYPE].join(',');
  }

  constructor(isSeen = false, isRead = false, private context: CommunicationNotificationItemContext) {
    super(CommunicationNotificationItem.NOTIFICATION_TYPE, isSeen, isRead);

    this.id = this.context.activity.group || this.id; // if the activity doesn't have a group we use the generated id

    this.isMarkedReadOnClick = false; // for chat messages the feature itself is in charge of marking things read

    this.aggregationKey = JSON.stringify({
      topicUid: this.context.activity.topicUid,
      threadOwnerUid: this.context.activity.threadOwnerUid
    });

    this.sectionToDetailMap = new Map();
    let sectionUids: Set<string> = new Set(this.context.activity.message.associatedSectionUids || []);

    if (this.context.senderInfo == null) {
      console.error('this context does not have senderInfo');
      return;
    }

    for (let section of this.context.senderInfo.sections) {
      if (!sectionUids.has(section.uid)) {
        continue;
      }
      this.sectionToDetailMap.set(section.uid, section);
    }
  }

  getTitle(): string {
    if (this.context.senderInfo == null) {
      return 'Display Name Not Available';
    }
    return this.context.senderInfo.firstName + ' ' + this.context.senderInfo.lastName;
  }

  getSections(): Array<SectionBasicRI> {
    return Array.from(this.sectionToDetailMap.values());
  }

  getPostedAt(): Date | null {
    return new Date(this.context.activity.message.sentAt!); // sent at is nullable for posting. It will have a value here.
  }

  getThreadType(): number {
    return this.context.activity.threadType;
  }

  getUserType(): string {
    return this.context.userType;
  }

  getTopicUid(): string {
    return this.context.activity.topicUid;
  }

  getTopicOrOwnerUid(): string {
    if (this.context.userType === 'TEACHER') {
      return this.context.activity.topicUid;
    }
    return this.context.activity.threadOwnerUid;
  }

  getTooltip(): string {
    return pdsI18n.getText('pds|notifications|Messages regarding xx', { name: this.getTitle() });
  }

  getThreadUid(): string {
    return this.context.activity.message.threadUid;
  }

  get lookUpKey(): string {
    return CommunicationNotificationItem.generateLookUpKey(this.context.activity.topicUid, this.context.activity.threadOwnerUid);
  }

  get message(): ChatMessageRI {
    return this.context.activity.message;
  }

  get threadUid(): string {
    return this.context.activity.message.threadUid;
  }

  get currentUserUid(): string {
    return this.context.currentUserUid;
  }

  get getstreamGroupId(): string {
    return this.context.getStreamGroupId;
  }
}
